import React, { lazy, Suspense, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { DollarSign, ShoppingCart, Users, Package, Clock, AlertTriangle, Plus, ArrowRight } from "lucide-react";
import { api, money } from "@/lib/api";
import StatCard from "@/components/admin/StatCard";
import { useLang } from "@/i18n";
const SalesOverview = lazy(() => import("@/components/admin/SalesOverview"));
import EmptyState from "@/components/admin/EmptyState";

const TAB_ROUTES = {
  products: "/admin/products", categories: "/admin/categories", orders: "/admin/orders",
  customers: "/admin/customers", services: "/admin/services", content: "/admin/content",
};

const STATUS_STYLES = {
  paid: "bg-palm-light text-palm-500", delivered: "bg-palm-light text-palm-500", shipped: "bg-ocean/10 text-ocean",
  processing: "bg-gold/10 text-gold-600", pending: "bg-amber-50 text-amber-600",
  cancelled: "bg-red-50 text-red-500", refunded: "bg-red-50 text-red-500",
};

export default function Dashboard() {
  const { t } = useLang();
  const [s, setS] = useState(null);
  const [status, setStatus] = useState("loading");
  const [errorStatus, setErrorStatus] = useState(null);
  const [requestVersion, setRequestVersion] = useState(0);
  const [params] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    const legacyTab = params.get("tab");
    if (legacyTab && TAB_ROUTES[legacyTab]) navigate(TAB_ROUTES[legacyTab], { replace: true });
  }, [params, navigate]);

  useEffect(() => {
    const controller = new AbortController();
    let retryTimer;
    setStatus("loading");
    setErrorStatus(null);
    const load = async (attempt = 0) => {
      try {
        const { data } = await api.get("/admin/stats", {
          timeout: 20000, signal: controller.signal,
        });
        if (controller.signal.aborted) return;
        setS(data);
        setStatus("success");
      } catch (error) {
        if (controller.signal.aborted) return;
        const code = error.response?.status;
        const transient = !code || [502, 503, 504].includes(code);
        if (transient && attempt < 2) {
          retryTimer = setTimeout(() => load(attempt + 1), 1000 * (attempt + 1));
          return;
        }
        setErrorStatus(code);
        setStatus("error");
      }
    };
    load();
    return () => { controller.abort(); clearTimeout(retryTimer); };
  }, [requestVersion]);

  if (status === "error") {
    return (
      <div data-testid="admin-dashboard" role="alert" className="rounded-2xl border border-border bg-white p-6">
        <h1 className="font-display text-2xl font-bold text-navy">Dashboard unavailable</h1>
        <p className="my-4 text-muted-foreground">
          {errorStatus === 401 ? "Your session has expired. Please sign in again."
            : errorStatus === 403 ? "You do not have permission to view this dashboard."
            : "We couldn't load the dashboard. Please try again."}
        </p>
        {errorStatus === 401 ? <Link to="/login" className="btn-gold rounded-full px-4 py-2">Sign in</Link>
          : errorStatus === 403 ? <Link to="/" className="btn-gold rounded-full px-4 py-2">Back to store</Link>
          : <button className="btn-gold rounded-full px-4 py-2" onClick={() => {
            setStatus("loading"); setRequestVersion((value) => value + 1);
          }}>Retry</button>}
      </div>
    );
  }

  if (status === "loading") {
    return (
      <div data-testid="admin-dashboard">
        <div className="mb-6 h-8 w-48 animate-pulse rounded bg-navy/10" />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-24 animate-pulse rounded-2xl bg-white/60" />)}
        </div>
      </div>
    );
  }

  const kpis = [
    { label: "Revenue", value: money(s.revenue), icon: DollarSign, colorClass: "text-palm-500" },
    { label: "Orders", value: s.total_orders, icon: ShoppingCart, colorClass: "text-ocean" },
    { label: "Customers", value: s.customers, icon: Users, colorClass: "text-sunset" },
    { label: "Products", value: s.products, icon: Package, colorClass: "text-gold-600" },
    { label: "Unpaid Orders", value: s.pending_orders, icon: Clock, colorClass: "text-amber-600" },
    { label: "Low Stock", value: s.low_stock.length, icon: AlertTriangle, colorClass: "text-red-500" },
  ];

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-3xl font-bold text-navy">Dashboard</h1>
        <div className="flex gap-2">
          <Link to="/admin/products" className="btn-gold flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold">
            <Plus size={15} /> New Product
          </Link>
          <Link to="/admin/orders" className="flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2 text-sm font-semibold text-navy hover:border-gold">
            View Orders <ArrowRight size={15} />
          </Link>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {kpis.map((k) => <StatCard key={k.label} {...k} />)}
      </div>

      <Suspense fallback={<p role="status" className="mt-6">{t("sales.loading")}</p>}><SalesOverview series={s.sales_series} /></Suspense>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <div className="rounded-2xl border border-border bg-white p-5 card-shadow lg:col-span-2">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-head font-bold text-navy">Recent Orders</h3>
            <Link to="/admin/orders" className="text-xs font-semibold text-gold-600 hover:underline">View all</Link>
          </div>
          {s.recent_orders.length === 0 ? (
            <EmptyState title="No orders yet" hint="New orders will show up here." />
          ) : (
            <div className="space-y-1">
              {s.recent_orders.map((o) => (
                <div key={o.id} className="flex items-center justify-between border-b border-border py-2.5 text-sm last:border-0">
                  <span className="font-medium text-navy">{o.order_number}</span>
                  <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${STATUS_STYLES[o.status] || "bg-muted text-muted-foreground"}`}>{o.status}</span>
                  <span className="font-semibold text-navy">{money(o.total)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-2xl border border-border bg-white p-5 card-shadow">
          <h3 className="mb-4 flex items-center gap-2 font-head font-bold text-navy"><AlertTriangle size={16} className="text-red-500" /> Low Stock</h3>
          {s.low_stock.length === 0 ? (
            <EmptyState title="All stocked up" hint="No products under 10 units." />
          ) : (
            <div className="space-y-2">
              {s.low_stock.map((p) => (
                <Link key={p.id} to="/admin/products" className="flex items-center justify-between border-b border-border py-2 text-sm last:border-0 hover:text-gold-600">
                  <span className="truncate text-navy">{p.name_en}</span>
                  <span className="font-semibold text-red-500">{p.stock} left</span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 rounded-2xl border border-border bg-white p-5 card-shadow">
        <h3 className="mb-4 font-head font-bold text-navy">Best Sellers</h3>
        {s.best_sellers.length === 0 ? (
          <EmptyState title="No sales yet" hint="Top-selling products will appear here once orders are paid." />
        ) : (
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {s.best_sellers.map((b, i) => (
              <div key={i} className="flex items-center justify-between rounded-xl bg-cream px-3 py-2 text-sm">
                <span className="truncate text-navy">{b.name}</span>
                <span className="font-semibold text-navy">{b.qty} sold</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
